/**
 * Chat read cursors, shared by oem's and the portal's chat.
 *
 * Moved out of oem's `~/hooks/_pubnub-unread-messages` because the map below is
 * runtime state both apps must share, not logic either can copy. oem's
 * background unread recount reads it to skip channels marked read after the
 * recount started — without that, a recount holding the pre-read cursor puts
 * the badge back. If the portal marked channels read through its own copy of
 * this map, oem's recount would never see those reads, and badges the user had
 * just cleared would reappear.
 */

/** Wall-clock of the most recent local mark-as-read, per channel id. */
export const lastMarkedReadAt = new Map<string, number>();

type MembershipClient = {
  objects: {
    getMemberships: (params: {
      filter: string;
      limit: number;
    }) => Promise<{ data?: unknown[] } | undefined>;
    setMemberships: (params: {
      channels: Array<{ id: string; custom?: Record<string, unknown> }>;
      limit: number;
      include: { customFields: boolean; totalCount: boolean };
    }) => Promise<unknown>;
  };
};

/**
 * Persist a channel's read cursor. Unconditional on purpose: skipping it when
 * the local badge already read zero left the server-side cursor behind, and the
 * next recount — which counts from that cursor — resurrected read messages.
 *
 * Only ever MOVES an existing cursor; it never joins a channel. PubNub's
 * `setMemberships` creates the membership when there is none, and who is a
 * member is the backend's decision: a membership is what the unread badge
 * counts. An OEM owner can open a ticket a portal has taken over (from its
 * History) and read or answer in it, and marking that read used to make them
 * a member — so every later portal message badged them for a ticket none of
 * their lists show. Asked of the server rather than the local map, which can
 * lag a membership the backend has just granted. (ENG-1787)
 */
export const markChannelRead = async (
  instance: MembershipClient | null | undefined,
  channel: { id: string; custom?: Record<string, unknown> } | null | undefined,
): Promise<void> => {
  if (!instance || !channel?.id) return;
  lastMarkedReadAt.set(channel.id, Date.now());
  try {
    const existing = await instance.objects.getMemberships({
      filter: `channel.id == '${channel.id}'`,
      limit: 1,
    });
    if (!existing?.data?.length) return;

    await instance.objects.setMemberships({
      channels: [channel],
      limit: 1,
      include: { customFields: true, totalCount: true },
    });
  } catch (error) {
    // Callers mark-as-read fire-and-forget. The badge is already 0 locally;
    // only the server-side cursor is behind.
    console.error("[unread] setMemberships failed", { channel, error });
  }
};
